import { type ReactNode, useEffect, useState } from "react";

import {
  Box,
  Button,
  Loader,
  Stack,
  Typography,
  useMediaQuery,
} from "@braintrust/braintrust-ui-components";
import {
  HelpOutlineIcon,
  StarSVGIcon,
} from "@braintrust/braintrust-ui-components/Icons";
import { useLoginPageStatsQuery } from "@js/apps/common/api";
import { useSetBgOnBody } from "@js/apps/common/hooks/use-set-bg-on-body";
import { RouterLink } from "@js/components/link";
import { Logo } from "@js/components/logo";
import { CoreLayout } from "@js/layouts/core";
import type { LoginPageStats } from "@js/types/common";

import { openOnboardingConfirmationPopup } from "../confirmation-popup";

import styles from "./styles.module.scss";

type AuthContainerProps = {
  children: ReactNode;
  pageTitle: string;
  isSignupPage?: boolean;
  isClientSignupPage?: boolean;
};

const bg = `linear-gradient(0deg, #EDE3FA, #EDE3FA), radial-gradient(34.72% 31.82% at 88% 92%, rgba(255, 244, 227, 0.55) 0%, rgba(255, 244, 227, 0) 70%), radial-gradient(41.67% 36.36% at 12% 75%, rgba(213, 181, 245, 0.4) 0%, rgba(213, 181, 245, 0) 70%), radial-gradient(76.39% 63.64% at 50% -15%, rgba(213, 181, 245, 0.55) 0%, rgba(213, 181, 245, 0) 65%)`;

const getStats = (loginPageStats?: LoginPageStats) => {
  if (!loginPageStats) {
    return;
  }

  return [
    { number: loginPageStats?.total_payment, label: "Paid to talent" },
    { number: loginPageStats?.employers_count, label: "Client teams" },
    { number: loginPageStats?.openings_number, label: "Open roles" },
  ];
};
export const AuthContainer = ({
  pageTitle,
  children,
  isClientSignupPage,
  isSignupPage,
}: AuthContainerProps) => {
  useSetBgOnBody({ bg: "#EDE3FA" });
  const { data: loginPageStats } = useLoginPageStatsQuery();
  const [stats, setStats] = useState(getStats(loginPageStats));

  const showHireTalent = isSignupPage && !isClientSignupPage;
  useEffect(() => {
    if (!loginPageStats || !!stats) {
      return;
    }
    // memoize stats, as logging in clears api state
    setStats(getStats(loginPageStats));
  }, [loginPageStats, stats]);

  return (
    <CoreLayout pageTitle={pageTitle}>
      <Stack
        sx={{ minHeight: "100vh", pb: 4, background: bg }}
        className={styles.container}
      >
        <Header showHireTalent={showHireTalent} />
        <Stack
          direction={{
            xs: "column",
            md: "row",
          }}
          sx={{
            width: "min(1220px, 100vw - 2rem)",
            marginInline: "auto",
            justifyContent: "space-between",
            gap: 4,
          }}
        >
          <Stack sx={{ flex: 3, pt: { xs: 0, sm: 3, md: 4 } }}>
            <Stack sx={{ maxWidth: "560px" }}>
              <Stack
                direction="row"
                sx={{
                  alignItems: "center",
                  alignSelf: "start",
                  background: "var(--white)",
                  borderRadius: "100px",
                  px: 2,
                  py: 0.75,
                  mb: 3,
                  gap: 1,
                  boxShadow: "0px 1px 2px 0px #1414140A",
                }}
              >
                <StarSVGIcon
                  sx={{
                    fontSize: "14px",
                    color: "var(--dark-violet)",
                    mb: "2px",
                  }}
                />

                <Typography
                  component="span"
                  fontWeight={500}
                  sx={{
                    fontSize: "12px !important",
                    letterSpacing: "1.2px !important",
                    textTransform: "uppercase",
                    color: "var(--dark-violet)",
                  }}
                >
                  Welcome to Braintrust
                </Typography>
              </Stack>
              <AuthHeroTitle isClientSignup={!!isClientSignupPage} />

              <Stack
                direction="row"
                sx={{
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: { xs: 3, sm: 4, md: 5 },
                  pt: { xs: 3, sm: 4 },
                  borderTop: "1px solid #14141414",
                  minHeight: 56,
                }}
              >
                {!stats ? (
                  <Loader size={28} />
                ) : (
                  stats.map(({ number, label }) => (
                    <Stack key={label} sx={{ gap: 0.5 }}>
                      <Typography
                        component="span"
                        size="large"
                        fontWeight={500}
                        sx={{
                          lineHeight: "1 !important",
                          fontSize: {
                            xs: "24px !important",
                            sm: "28px !important",
                            md: "32px !important",
                          },
                          mb: 0.75,
                        }}
                      >
                        {number}
                      </Typography>
                      <Typography
                        component="span"
                        fontWeight={500}
                        sx={{
                          fontSize: {
                            xs: "10px !important",
                            sm: "11px !important",
                          },
                          lineHeight: "1 !important",
                          textTransform: "uppercase",
                          letterSpacing: "1.2px",
                          color: "var(--grey-2)",
                        }}
                      >
                        {label}
                      </Typography>
                    </Stack>
                  ))
                )}
              </Stack>
            </Stack>
          </Stack>
          <Stack
            sx={{
              alignItems: "center",
              justifyContent: "center",
              width: "min(548px, 100vw - 2rem)",
              flexShrink: 0,
            }}
          >
            <Stack
              sx={{
                background: "var(--white)",
                borderRadius: { xs: "14px", sm: "20px", md: "24px" },
                width: "100%",
                p: { xs: 3, sm: 4, md: 5 },
                pb: { xs: 2, sm: 3, md: 4 },
                boxShadow: `0px 8px 16px -8px #1414140F, 0px 24px 48px -16px #5D19742E, 0px 1px 3px 0px #1414140A;`,
              }}
            >
              {children}
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </CoreLayout>
  );
};

const Header = ({ showHireTalent }: { showHireTalent?: boolean }) => {
  const isMobile = useMediaQuery("sm");
  const hideNeedHelpButton = isMobile && showHireTalent;
  return (
    <Box
      sx={{
        px: { xs: 2, sm: 3 },
        mb: { xs: 2, sm: 3, md: 5, lg: 7.5 },
      }}
    >
      <Stack
        direction="row"
        sx={{
          justifyContent: "space-between",
          height: "100%",
          alignItems: "center",
          gap: 2,
          minHeight: "72px",
          flexWrap: "wrap",
          py: "15px",
        }}
      >
        <Logo className={styles.logo} />
        <Stack
          direction="row"
          sx={{ gap: 1, alignItems: "center", ml: "auto" }}
        >
          {showHireTalent && (
            <Button
              variant="transparent"
              shape="squared"
              onClick={() => {
                openOnboardingConfirmationPopup();
              }}
            >
              Hire talent
            </Button>
          )}
          {!hideNeedHelpButton && (
            <Button
              variant="white-violet"
              shape="squared"
              startIcon={<HelpOutlineIcon />}
              RouterLink={RouterLink}
              href={SETTINGS.SUPPORT_URLS.HELP_CENTER_URL}
              target="_blank"
              rel="noopener noreferrer"
              size={isMobile ? "x-small" : "small"}
            >
              Need help?
            </Button>
          )}
        </Stack>
      </Stack>
    </Box>
  );
};

const fontSize = { xs: "36px !important", md: "60px !important" } as const;
const subtitleStyles = {
  color: "var(--grey-1)",
  maxWidth: "440px",
  mb: { xs: 3, sm: 4.5 },
  lineHeight: "1.52 !important",
  fontSize: {
    xs: "14px !important",
    md: "17px !important",
  },
} as const;

const AuthHeroTitle = ({ isClientSignup }: { isClientSignup: boolean }) => {
  if (isClientSignup) {
    return (
      <>
        <Typography
          component="h1"
          variant="display"
          size="small"
          fontWeight={500}
          sx={{
            mb: 2,
            fontSize,
            lineHeight: "1.1 !important",
          }}
        >
          Accelerate your{" "}
          <Typography
            component="span"
            variant="title"
            size="large"
            fontWeight={500}
            sx={{
              color: "var(--dark-violet)",
              fontSize,
              lineHeight: "1.1 !important",
            }}
          >
            hiring.
          </Typography>
        </Typography>
        <Typography
          component="p"
          variant="paragraph"
          size="medium"
          fontWeight={400}
          sx={subtitleStyles}
        >
          Find top-tier talent on Braintrust in minutes. Connect with
          exceptional candidates ready to make an impact, without the hassle of
          assessments.
        </Typography>
      </>
    );
  }

  return (
    <>
      <Typography
        component="h1"
        variant="display"
        size="small"
        fontWeight={500}
        sx={{
          mb: 2,
          fontSize,
          lineHeight: "1.1 !important",
        }}
      >
        Find work with the
        <br />
        <Typography
          component="span"
          variant="title"
          size="large"
          fontWeight={500}
          sx={{
            color: "var(--dark-violet)",
            fontSize,
            lineHeight: "1.1 !important",
          }}
        >
          world's leading companies.
        </Typography>
      </Typography>
      <Typography
        component="p"
        variant="paragraph"
        size="medium"
        fontWeight={400}
        sx={subtitleStyles}
      >
        Get matched to roles that fit. Your profile lands in front of clients
        hiring across engineering, design, product, and creative.
      </Typography>
    </>
  );
};
